import { message } from 'antd';
import { routerRedux } from 'dva';
import jsSHA from 'jssha';

import { apiPaths } from '../config/api-paths.js';
import { checkSchoolName, checkSlug, getSession, registerMessage } from '../services/register.js';
import { responseCode, responseMessage } from '../types/domain.js';
import type { RegistrationSchoolInfo, SchoolTypeTrack } from '../types/domain.js';
import { isRecord } from './dva-types.js';
import type { ModelSaga, ModelCommands, TypedModel } from './dva-types.js';

export interface RegisterState {
  readonly captchaUrl?: string;
  readonly ifDoubleClick?: boolean;
  readonly deploymentStyle?: string;
  readonly ifShow?: boolean;
}

function isSessionWithDeploymentStyle(value: unknown): value is { deploymentStyle?: string } {
  return (
    !(value instanceof Error) &&
    isRecord(value) &&
    (value['deploymentStyle'] === undefined || typeof value['deploymentStyle'] === 'string')
  );
}

function isRegisterState(value: unknown): value is RegisterState {
  return (
    isRecord(value) &&
    (value['captchaUrl'] === undefined || typeof value['captchaUrl'] === 'string') &&
    (value['ifDoubleClick'] === undefined || typeof value['ifDoubleClick'] === 'boolean') &&
    (value['deploymentStyle'] === undefined || typeof value['deploymentStyle'] === 'string') &&
    (value['ifShow'] === undefined || typeof value['ifShow'] === 'boolean')
  );
}

const keyList = [
  'fullName',
  'logo',
  'slug',
  'administrationID',
  'computers',
  'users',
  'schoolTypeTracks',
  'registerName',
  'registerEmail',
  'registerCellphone',
  'registerQQ',
  'registerPassword',
  'registerCertifcate',
  'captcha',
  'passwordValidate',
] as const satisfies readonly (keyof RegistrationSchoolInfo)[];

const keyMap: Partial<Record<(typeof keyList)[number], string>> = {
  fullName: '学校全称',
  slug: '学校空间地址',
  computers: '机房计算机总数',
  registerName: '教师姓名',
  registerEmail: '邮箱',
  registerCellphone: '手机号码',
  registerPassword: '密码',
  captcha: '图形验证码',
  passwordValidate: '验证密码',
};

const passwordPattern = new RegExp(
  '^(?![A-Za-z0-9]+$)(?![a-z0-9\\W]+$)(?![A-Za-z\\W]+$)(?![A-Z0-9\\W]+$)[a-zA-Z0-9\\W]{8,20}$',
);
const disabledSlugs = [
  'www',
  'yopu',
  'test',
  'ceshi',
  'hxr',
  'static',
  'cdn',
  'admin',
  'manager',
  'jiaoyan',
  'jy',
];

function isPhoneNumber(value: unknown): boolean {
  return /^0?1[3|4|5|6|7|8|9][0-9]\d{8}$/u.test(String(value ?? ''));
}

function isEmail(value: unknown): boolean {
  return /^([a-zA-Z]|[0-9])(\w|-)+@[a-zA-Z0-9]+\.([a-zA-Z]{2,4})$/u.test(String(value ?? ''));
}

function isName(value: unknown): boolean {
  return /^[\u4e00-\u9fa5_a-zA-Z]{2,20}$/u.test(String(value ?? ''));
}

function isSlug(value: unknown): boolean {
  return /^([a-z][a-z0-9]+)$/u.test(String(value ?? ''));
}

function isInt(value: unknown): boolean {
  return /^[1-9]\d*|0$/u.test(String(value ?? ''));
}

function isQQ(value: unknown): boolean {
  return /^[1-9][0-9]{4,9}$/u.test(String(value ?? ''));
}

function resetDoubleClick(put: ModelCommands['put']): unknown {
  return put({ type: 'update', payload: { ifDoubleClick: false } });
}

const model: TypedModel<RegisterState> = {
  namespace: 'register',
  state: {},
  subscriptions: {
    setup({ dispatch, history }) {
      history.listen(({ pathname }) => {
        if (pathname === '/register') dispatch({ type: 'initialization' });
      });
    },
  },
  effects: {
    *initialization(_, { put }): ModelSaga {
      const session = yield getSession();
      const deploymentStyle = isSessionWithDeploymentStyle(session)
        ? session.deploymentStyle
        : undefined;
      yield put({
        type: 'update',
        payload: {
          captchaUrl: `${apiPaths.registerCaptcha}?d=${Math.random()}`,
          ifDoubleClick: false,
          deploymentStyle,
        },
      });
    },

    *onSave({ payload }, { put, select }): ModelSaga {
      const selectedState = yield select(
        (globalState: { register: RegisterState }) => globalState.register,
      );
      if (!isRegisterState(selectedState)) {
        throw new TypeError('Invalid register state');
      }
      const registerState = selectedState;
      if (registerState.ifDoubleClick) return;

      const schoolInfo = ((payload as { schoolInfo?: RegistrationSchoolInfo } | undefined)
        ?.schoolInfo ?? {}) as RegistrationSchoolInfo;
      const {
        fullName,
        slug,
        administrationID,
        registerEmail,
        registerCellphone,
        passwordValidate,
        registerPassword,
        schoolTypeTracks = [],
        registerName,
        computers,
        users = 0,
        registerQQ,
      } = schoolInfo;

      yield put({ type: 'update', payload: { ifDoubleClick: true } });

      const createMap: RegistrationSchoolInfo = {};
      for (const key of keyList) {
        const value = schoolInfo[key];
        if (!value && value !== undefined) {
          message.error(`请将${keyMap[key]}填写完整`);
          yield resetDoubleClick(put);
          return;
        }
        createMap[key] = value as never;
      }

      const schoolNameResponse = yield checkSchoolName({ fullName, administrationID });
      if (responseCode(schoolNameResponse) !== 0) {
        message.error(responseMessage(schoolNameResponse));
        yield resetDoubleClick(put);
        return;
      }

      if (!isSlug(slug)) {
        message.error('学校空间地址不正确，请重新填写');
        yield resetDoubleClick(put);
        return;
      }

      if (String(slug).length > 8) {
        message.error('前缀长度大于8个字符，请重新填写');
        yield resetDoubleClick(put);
        return;
      }

      if (disabledSlugs.includes(String(slug))) {
        message.error('填写的学校空间地址已禁用，请重新填写');
        yield resetDoubleClick(put);
        return;
      }

      const slugResponse = yield checkSlug({ slug });
      if (responseCode(slugResponse) !== 0) {
        message.error(responseMessage(slugResponse));
        yield resetDoubleClick(put);
        return;
      }

      for (const track of schoolTypeTracks as SchoolTypeTrack[]) {
        const gradeSum = track.gradeNums.reduce((sum, current) => sum + current, 0);
        if (gradeSum === 0) {
          message.error(`请将${track.name}阶段中的年级人数填写完整`);
          yield resetDoubleClick(put);
          return;
        }
        if (!isInt(gradeSum)) {
          message.error('请填写正确的人数');
          yield resetDoubleClick(put);
          return;
        }
      }

      if (!isName(registerName) && registerName) {
        message.error('请填写正确的教师姓名');
        yield resetDoubleClick(put);
        return;
      }
      if (!isInt(computers) && computers) {
        message.error('请填写正确的机房计算机总数');
        yield resetDoubleClick(put);
        return;
      }
      if (!isInt(users) && users) {
        message.error('请填写正确的预计用户数');
        yield resetDoubleClick(put);
        return;
      }
      if (!isEmail(registerEmail) && registerEmail) {
        message.error('邮箱格式不正确');
        yield resetDoubleClick(put);
        return;
      }
      if (!isQQ(registerQQ) && registerQQ) {
        message.error('请填写正确的QQ号码');
        yield resetDoubleClick(put);
        return;
      }
      if (!isPhoneNumber(registerCellphone) && registerCellphone) {
        message.error('请填写正确的手机号码');
        yield resetDoubleClick(put);
        return;
      }
      if (passwordValidate !== registerPassword) {
        message.error('验证密码不通过，请检查密码是否正确');
        yield resetDoubleClick(put);
        return;
      }
      if (!registerPassword) {
        message.error('请填写密码');
        yield resetDoubleClick(put);
        return;
      }
      if (registerPassword.length > 16 || registerPassword.length < 8) {
        message.error('密码长度8-16位');
        yield resetDoubleClick(put);
        return;
      }
      if (!passwordPattern.test(registerPassword)) {
        message.error('密码必须包含数字、大小写英文字母和特殊字符');
        yield resetDoubleClick(put);
        return;
      }

      const sha = new jsSHA('SHA-1', 'TEXT');
      sha.update(registerPassword);
      const response = yield registerMessage({
        ...createMap,
        registerPassword: sha.getHash('HEX'),
        computers: Number.parseInt(String(computers), 10),
        users: Number.parseInt(String(users), 10),
      });
      if (responseCode(response) !== 0) {
        message.error(responseMessage(response));
        yield resetDoubleClick(put);
        return;
      }
      yield put(routerRedux.push('/result'));
    },
  },
  reducers: {
    update(state: RegisterState, { payload }: { payload: Partial<RegisterState> }): RegisterState {
      return { ...state, ...payload };
    },
  },
};

export default model;
